  <template>
  <div class="asset-item position-relative mx-2 mt-2 mb-0 rounded"
       :id="componentId"
       v-observe-visibility="onVisibilityChanged">
    <MediaContent :posterImage="item.pollMetadata.images.abovePoll"
                  :programmeId="item.pollMetadata.general.videoProgrammeId"
                  :keepInitiated="promoMode"
                  :isLocked="isLocked"
                  :isVisible="isVisible"
                  :ref="`${this.componentId}_mediaContent`"
                  @contentClicked="onContentClicked"/>
    <div class="asset-badge fas fa-lock" v-show="isLocked"></div>
    <div class="pb-3 pl-3 pr-3 pt-2 poll-background">
      <p id="title">{{item.title}}</p>

      <div id="expanded">
        <span v-show="!isThePollReady && !pollError">Loading poll details...</span>
        <span v-show="pollError" v-html="errorMessage" />
        <vue-poll v-show="isThePollReady && !pollError" v-bind="pollOptions" @addvote="addVote" :key="refreshCounter"/>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { spinnakerApi } from '../js/SpinnakerApi.js'
import { ObserveVisibility } from 'vue-observe-visibility'
import MediaContent from '../components/MediaContent'
import VuePoll from '../components/Poll.vue'

export default {
  page: 'poll',
  directives: { 'observe-visibility': ObserveVisibility },
  components: {
    MediaContent,
    VuePoll
  },
  data: function () {
    return {
      componentId: `poll_${this.item.id}`,
      isVisible: false,
      isThePollReady: false,
      pollError: false,
      refreshCounter: 0,
      errorMessage: 'Something went wrong with this poll',
      pollOptions: null
    }
  },
  props: ['item', 'isShared', 'isOpenForAnonymous', 'promoMode'],
  computed: {
    ...mapState({
      isBeta: state => state.isBeta,
      isRegisteredUser: state => state.isRegisteredUser
    }),
    isLocked: function () {
      if (this.isOpenForAnonymous && !this.isBeta) {
        return false
      }
      if (this.isShared) {
        return false
      }
      if (this.item.isPlaceholder) {
        return false
      }
      if (this.isBeta && !this.isRegisteredUser) {
        return true
      }
      const signUpRequired = this.item.flags && this.item.flags.indexOf('SignupRequired') !== -1
      return signUpRequired && !this.isRegisteredUser
    }
  },
  created () {
    if (this.item.isPlaceholder) {
      return
    }

    this.resetPollOptions()

    this.storeUnsubscribe = this.$store.subscribe((mutation, state) => {
      switch (mutation.type) {
        case 'isSignedInUser':
        case 'isRegisteredUser':
          if (state.isSignedInUser) {
            this.pollData()
          }
          break
      }
    })
    if (this.$store.state.isSignedInUser) {
      this.pollData()
    }
  },
  methods: {
    resetPollOptions () {
      this.isThePollReady = false
      this.pollError = false
      this.pollOptions = {
        question: this.item.pollMetadata.poll.pollQuestion,
        submitButtonMode: true,
        submitButtonText: 'Vote',
        answers: [],
        isLocked: this.isLocked,
        isRegisteredUser: this.isRegisteredUser,
        finalResults: this.isThePollClosed(this.item.pollMetadata.general.votingEnds),
        votingEnds: this.item.pollMetadata.general.votingEnds,
        isOpenForAnonymous: this.isOpenForAnonymous
      }
    },
    pollData () {
      this.resetPollOptions()
      console.info('SimplePoll.pollData: Called for poll ' + this.item.pollMetadata.poll.pollQuestion)
      const options = this.item.pollMetadata.poll.pollOptions
      let answers = []
      for (const key in options) {
        if (Array.isArray(options[key]) && options[key].length > 1) {
          const answer = { value: options[key][0], text: options[key][1], votes: 0, selected: false }
          answers.push(answer)
        }
      }
      this.pollOptions.answers = answers
      this.getVotes()
    },
    async getVotes () {
      Promise.all([spinnakerApi.getMyPoll(this.item.id), spinnakerApi.getPollStats(this.item.id)]).then(response => {
        let userHasVoted = false
        let isThePollClosed = false
        // if there is any button selected before making a call to the backend
        // user will end up having 2 answers marked as answered
        // https://www.pivotaltracker.com/story/show/172429543
        this.pollOptions.answers.forEach(element => {
          element.selected = false
        })
        if (response[0].data && response[0].data.selectedOption) {
          let found = this.pollOptions.answers.find(
            element => element.value === response[0].data.selectedOption
          )
          if (found) {
            found.selected = true
            userHasVoted = true
          }
        }

        if (Array.isArray(response) && response.length === 2 && response[1].data && response[1].data.split) {
          for (const key in response[1].data.split) {
            let found = this.pollOptions.answers.find(element => element.value === key)
            if (found) {
              found.votes = response[1].data.split[key]
            }
          }
          this.pollOptions.finalResults = this.pollOptions.finalResults || userHasVoted
          this.refreshCounter++
        }
        console.info(`${this.item.pollMetadata.poll.pollQuestion}isthepollclosed=${isThePollClosed}  and userHasVOted=${userHasVoted} .. finalResults=${this.pollOptions.finalResults}`)
        this.isThePollReady = true
      }).catch((error) => {
        console.info(`SimplePoll.getVotes: Error in setting up pole "${this.item.pollMetadata.poll.pollQuestion}", error is: ${error}`)
        this.errorMessage = 'sorry, we couldn\'t get the details of this poll'
        this.pollError = true
      })
    },
    addVote (obj) {
      console.info('SimplePoll.addVote: You voted ' + obj.value + '!')
      spinnakerApi.addVote(this.item.id, obj.value).then(
        result => {
          console.info('SimplePoll.addVote: Vote added to the poll')
        }).catch(
        error => {
          this.errorMessage = this.errorResponseToDescription(error)
          this.pollError = true
        })
    },
    errorResponseToDescription (err) {
      let response = err.response
      let errorMessage = this.errorMessage
      console.info(`SimplePoll.errorResponseToDescription: Couldn't cast a vote: ${err}${
        response && response.status ? ' [' + response.status + ']' : ''
      }`)
      if (
        response &&
        response.status === 400 &&
        response.data &&
        response.data.message
      ) {
        switch (response.data.errorCode) {
          case 103:
            errorMessage = 'You have already voted in this poll!'
            break
          default:
            errorMessage = 'Sorry, there has been an error while saving your vote.'
        }
      }
      return errorMessage
    },
    isThePollClosed (pollCloseDate) {
      const closeDate = new Date(pollCloseDate)
      const now = new Date()

      return closeDate < now
    },

    onItemClick () {
      if (this.isLocked) {
        this.$store.commit('startSignIn', 'CONTENT_BLOCKED')
        return
      }
      this.$refs[`${this.componentId}_mediaContent`].onPosterClick()
    },

    onContentClicked () {
      if (this.isLocked) {
        this.$store.commit('startSignIn', 'CONTENT_BLOCKED')
      }
    },

    onVisibilityChanged (isVisible) {
      this.isVisible = isVisible
    }

  }
}
</script>
